import { useState } from "react";
import { useParams } from "react-router-dom";
import EmployeePageView from "./EmployeePageView";
import { useAuthContext } from "../../../context/AuthContext";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useRouteContext } from "../../../context/RouteContext";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";

function EmployeePage() {
  const callEvaApi = useEvaApi();
  const routeContext = useRouteContext();
  const { employee } = useParams();
  const authContext = useAuthContext();
  const [allergens, setAllergens] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [parkingLocations, setParkingLocations] = useState([]);
  const [employeeData, setEmployeeData] = useState({
    registration_code: "",
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    allergens: [],
    department_id: null,
    language_id: null,
    location_id: authContext.auth.location,
    emergency_response_officer: false,
    phone_number: "",
    communication: [],
    license_plate: "",
    parking_locations: [],
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * Initialize the component
   */
  useCustomEffect(() => {
    fetchAllergens();
    fetchDepartments();
    fetchParkingLocations();

    if (employee) {
      fetchEmployeeData();
    }
  });

  /**
   * This method will fetch the current departments from the API
   */
  async function fetchDepartments() {
    await callEvaApi(
      new EvaApiCall("departments")
        .setLoadingGroup("employee_page_fetch")
        .setOnSuccess((response) => {
          setDepartments(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the parking locations from the API
   */
  async function fetchParkingLocations() {
    await callEvaApi(
      new EvaApiCall("parking_locations")
        .setLoadingGroup("employee_page_fetch")
        .setOnSuccess((response) => {
          setParkingLocations(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * Fetch the allergens from the API
   */
  async function fetchAllergens() {
    await callEvaApi(
      new EvaApiCall("canteen/allergens")
        .setLoadingGroup("employee_page_fetch")
        .setOnSuccess((response) => {
          setAllergens(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the employee data from the API
   */
  async function fetchEmployeeData() {
    await callEvaApi(
      new EvaApiCall(`employees/${employee}`)
        .setLoadingGroup("employee_page_fetch")
        .setOnSuccess((response) => {
          setEmployeeData({
            registration_code: response.data.data.registration_code,
            first_name: response.data.data.first_name,
            last_name: response.data.data.last_name,
            title: response.data.data.title,
            email: response.data.data.email,
            allergens: response.data.data.allergens,
            location_id: response.data.data.location,
            department_id: response.data.data.department,
            language_id: response.data.data.language,
            emergency_response_officer:
              response.data.data.emergency_response_officer,
            phone_number: response.data.data.phone_number,
            communication: response.data.data.communication ?? [],
            license_plate: response.data.data.license_plate,
            parking_locations: response.data.data.parking_locations,
          });
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will update the location id on input change
   * @param {any} value
   */
  function onLocationIdChange(value) {
    setEmployeeData({ ...employeeData, location_id: value });
  }

  /**
   * This method will update the registration code on input change
   * @param {event} e
   */
  function onRegistrationCodeChange(e) {
    setEmployeeData({ ...employeeData, registration_code: e.target.value });
  }

  /**
   * This method will update the first name on input change
   * @param {event} e
   */
  function onFirstNameChange(e) {
    setEmployeeData({ ...employeeData, first_name: e.target.value });
  }

  /**
   * This method will update the last name on input change
   * @param {event} e
   */
  function onLastNameChange(e) {
    setEmployeeData({ ...employeeData, last_name: e.target.value });
  }

  /**
   * This method will update the title on input change
   * @param {event} e
   */
  function onTitleChange(e) {
    setEmployeeData({ ...employeeData, title: e.target.value });
  }

  /**
   * This method will update the email on input change
   * @param {event} e
   */
  function onEmailChange(e) {
    setEmployeeData({ ...employeeData, email: e.target.value });
  }

  /**
   * This method will update the phone number on input change
   * @param {event} e
   */
  function onPhoneNumberChange(e) {
    setEmployeeData({ ...employeeData, phone_number: e.target.value });
  }

  /**
   * This method will update the communication on input change
   * @param {array} values
   */
  function onCommunicationChange(values) {
    setEmployeeData({
      ...employeeData,
      communication: values,
    });
  }

  /**
   * This method will update the license plate on input change
   * @param {event} e
   */
  function onLicensePlateChange(e) {
    setEmployeeData({ ...employeeData, license_plate: e.target.value });
  }

  /**
   * This method will update the emergency response officer on input change
   * @param {bool} value
   */
  function onEmergencyResponseOfficerChange(value) {
    setEmployeeData({
      ...employeeData,
      emergency_response_officer: value,
    });
  }

  /**
   * This method will update the allergens on input change
   * @param {array} values
   */
  function onAllergensChange(values) {
    setEmployeeData({
      ...employeeData,
      allergens: values,
    });
  }

  /**
   * This method will update the department on input change
   * @param {any} value
   */
  function onDepartmentChange(value) {
    setEmployeeData({ ...employeeData, department_id: value });
  }

  /**
   * This method will update the language on input change
   * @param {any} value
   */
  function onLanguageChange(value) {
    setEmployeeData({ ...employeeData, language_id: value });
  }

  /**
   * This method will update the parking locations on input change
   * @param {any} value
   */
  function onParkingLocationsChange(value) {
    setEmployeeData({ ...employeeData, parking_locations: value });
  }

  /**
   * This method will handle the onCancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(employee ? `employees/${employee}` : "employees")
        .setLoadingGroup("employee_page_save")
        .setMethod(employee ? "PUT" : "POST")
        .setData(employeeData)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true),
    );
  }

  return (
    <EmployeePageView
      employee={employee}
      employeeData={employeeData}
      parkingLocations={parkingLocations}
      allergens={allergens}
      departments={departments}
      onLocationIdChange={onLocationIdChange}
      onRegistrationCodeChange={onRegistrationCodeChange}
      onFirstNameChange={onFirstNameChange}
      onLastNameChange={onLastNameChange}
      onTitleChange={onTitleChange}
      onEmailChange={onEmailChange}
      onAllergensChange={onAllergensChange}
      onDepartmentChange={onDepartmentChange}
      onLanguageChange={onLanguageChange}
      onEmergencyResponseOfficerChange={onEmergencyResponseOfficerChange}
      onPhoneNumberChange={onPhoneNumberChange}
      onCommunicationChange={onCommunicationChange}
      onLicensePlateChange={onLicensePlateChange}
      onParkingLocationsChange={onParkingLocationsChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default EmployeePage;
